"use client";

import { useState, useEffect } from "react";
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks } from "date-fns";
import { useOpenSlots } from "../../hooks/use-api";
import { useBookingMutation } from "../../hooks/useBookingMutation";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Button } from "../button";
import { Input } from "../input";
import { Label } from "../label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../select";
import { LoadingSpinner } from "../loading-spinner";
import { ChevronLeft, ChevronRight, Clock, Users, MapPin, Search, Calendar } from "lucide-react";
import { AdvancedBookingForm, TimeSlotGrid } from "../shared";
import { AccountRequiredModal } from "../AccountRequiredModal";
import { CreateMeetingModal } from "../..";
import { useToast } from "../../hooks/use-toast";
import { useAccountRequired } from "../../hooks/useAccountRequired";
import type { BookingData } from "../../types/calendar";

export default function BookingPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [bookeeEmail, setBookeeEmail] = useState("");
  const [meetingDuration, setMeetingDuration] = useState(30);
  const [showAvailability, setShowAvailability] = useState(false);
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const { toast } = useToast();
  const { hasAnyAccount, isLoading: accountLoading, showAccountModal, closeModal } = useAccountRequired();

  const weekStart = startOfWeek(currentDate);

  // Show loading state while checking account status
  if (accountLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking account status...</p>
        </div>
      </div>
    );
  }

  const {
    data: availability,
    isLoading,
    error,
    refetch: refetchAvailability,
  } = useOpenSlots({
    bookeeEmail: bookeeEmail,
    selectedWeek: currentDate,
    meetingDuration: meetingDuration,
    enabled: !!bookeeEmail && !!meetingDuration,
  });

  const bookingMutation = useBookingMutation();

  // Auto-show availability when data is available
  useEffect(() => {
    if (availability && availability.length > 0) {
      setShowAvailability(true);
    }
  }, [availability]);

  const goToPreviousWeek = () => {
    setCurrentDate(subWeeks(currentDate, 1));
  };

  const goToNextWeek = () => {
    setCurrentDate(addWeeks(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Refetch availability when week changes
  useEffect(() => {
    if (showAvailability) {
      refetchAvailability();
    }
  }, [currentDate, showAvailability, refetchAvailability]);

  const handleSlotSelect = (slot: string) => {
    setSelectedSlot(slot);
  };


  const handleBookingSubmit = async (data: BookingData) => {
    try {
      await bookingMutation.mutateAsync(data);
      setSelectedSlot(null);
      toast({
        title: "Booking Successful",
        description: "Your meeting has been scheduled successfully!",
      });
    } catch (error) {
      console.error("Booking failed:", error);
      throw error; // Re-throw to let the BookingForm handle the error
    }
  };

  // Reset availability when email or duration changes
  useEffect(() => {
    if (showAvailability) {
      setShowAvailability(false);
      setSelectedSlot(null);
    }
  }, [bookeeEmail, meetingDuration]);

  // Show loading state when we have a person selected but are still loading
  if (bookeeEmail && isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Book a Meeting</h1>
          <p className="text-gray-600 mb-4">
            Select the person you want to book with and their availability will be shown automatically
          </p>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading availability for {bookeeEmail.split('@')[0]}...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Book a Meeting</h1>
        <p className="text-gray-600 mb-4">
          Select the person you want to book with and their availability will be shown automatically
        </p>
        <Button
          variant="outline"
          onClick={() => setShowCreateMeeting(true)}
          className="flex items-center space-x-2 mx-auto"
        >
          <Calendar className="h-4 w-4" />
          <span>Create New Meeting</span>
        </Button>
      </div>

      {/* Email Input and Duration Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Select Person & Duration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="bookeeEmail">Person to Book With *</Label>
              <Select value={bookeeEmail} onValueChange={setBookeeEmail}>
                <SelectTrigger className="mt-1 bg-card border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                  <SelectValue placeholder="Select person to book with" />
                </SelectTrigger>
                <SelectContent className="bg-card border border-gray-200 shadow-lg z-[99999]">
                  <SelectItem value="eliza.keffeler@westtn.consulting" className="hover:bg-card-50 focus:bg-card-50">Eliza Keffeler</SelectItem>
                  <SelectItem value="charlie.keffeler@westtn.consulting" className="hover:bg-card-50 focus:bg-card-50">Charlie Keffeler</SelectItem>
                  <SelectItem value="oliviakeffeler@westtn.consulting" className="hover:bg-card-50 focus:bg-card-50">Olivia Keffeler</SelectItem>
                  {bookeeEmail === "miclainkeffeler@westtn.consulting" && (
                    <SelectItem value="miclainkeffeler@westtn.consulting" className="hover:bg-card-50 focus:bg-card-50">Miclain Keffeler</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="meetingDuration">Meeting Duration</Label>
              <Select value={meetingDuration.toString()} onValueChange={(value: string) => setMeetingDuration(parseInt(value))}>
                <SelectTrigger className="mt-1 bg-card border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border border-gray-200 shadow-lg z-[99999]">
                  <SelectItem value="15" className="hover:bg-card-50 focus:bg-card-50">15 minutes</SelectItem>
                  <SelectItem value="30" className="hover:bg-card-50 focus:bg-card-50">30 minutes</SelectItem>
                  <SelectItem value="45" className="hover:bg-card-50 focus:bg-card-50">45 minutes</SelectItem>
                  <SelectItem value="60" className="hover:bg-card-50 focus:bg-card-50">1 hour</SelectItem>
                  <SelectItem value="90" className="hover:bg-card-50 focus:bg-card-50">1.5 hours</SelectItem>
                  <SelectItem value="120" className="hover:bg-card-50 focus:bg-card-50">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                onClick={() => refetchAvailability()}
                disabled={!bookeeEmail || isLoading}
                variant="outline"
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner className="mr-2 h-4 w-4" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Calendar className="mr-2 h-4 w-4" />
                    Refresh Availability
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display - Show when we have a person selected and there's an error */}
      {bookeeEmail && error && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-red-600 mb-2">
                Error Loading Availability
              </h3>
              <p className="text-red-600">
                {(error as Error)?.message || "Failed to load calendar availability"}
              </p>
              <p className="text-sm text-red-500 mt-2">
                Make sure the email address is correct and the person has connected their calendar.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Week Navigation - Show when we have availability data */}
      {showAvailability && availability && availability.length > 0 && (
        <div className="flex justify-between items-center mb-6">
          <Button variant="outline" onClick={goToPreviousWeek}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-center">
            <h2 className="text-xl font-semibold">
              {format(weekStart, "MMM d")} - {format(endOfWeek(currentDate), "MMM d, yyyy")}
            </h2>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={goToToday}>
              Today
            </Button>
            <Button variant="outline" onClick={goToNextWeek}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Availability and Booking Section */}
      {showAvailability && availability && availability.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Availability Grid */}
          <Card>
            <CardContent className="p-6">
              <TimeSlotGrid
                availableDays={availability || []}
                selectedSlot={selectedSlot}
                onSlotSelect={(slot: { start: string; end: string; isLunchSlot?: boolean }) => handleSlotSelect(slot.start)}
                bookeeEmail={bookeeEmail}
              />
            </CardContent>
          </Card>

          {/* Booking Form */}
          <AdvancedBookingForm
            onSubmit={handleBookingSubmit}
            isLoading={bookingMutation.isPending}
            selectedSlot={selectedSlot}
            bookeeEmail={bookeeEmail}
          />
        </div>
      )}

      {/* Initial State - Show instructions when no person selected or no availability */}
      {(!bookeeEmail || (!showAvailability && !error)) && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">Ready to Book a Meeting?</h3>
              <p className="text-gray-600 mb-4">
                Select the person you want to book with above and choose your preferred meeting duration. 
                Their available time slots will be displayed automatically.
              </p>
              <div className="flex justify-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  <span>Select person</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>Choose time slot</span>
                </div>
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span>Book meeting</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Account Required Modal */}
      <AccountRequiredModal
        isOpen={showAccountModal}
        onClose={closeModal}
        title="Calendar Setup Required"
        description="You need to connect your calendar accounts to use the booking system."
      />
      
      {/* Create Meeting Modal */}
      <CreateMeetingModal
        isOpen={showCreateMeeting}
        onClose={() => setShowCreateMeeting(false)}
        initialDate={currentDate}
      />
    </div>
  );
}
